import { createSelector } from 'reselect';

import { selectFormBundles } from 'store/forms/selectors';

import { selectLoanApplication } from './loanApplication.selectors';

const selectLoanBundleHistoryState = createSelector(
  selectLoanApplication,
  ({ loanBundleHistory }) => loanBundleHistory,
);

const selectLoanBundleHistory = createSelector(
  selectLoanBundleHistoryState,
  loanBundleHistory => loanBundleHistory?.items,
);

// SETTLED (succeeded or failed) — NOT "loaded". Callers can tell "no bundles delivered" apart from
// "not fetched yet" instead of reading an unfetched empty list as an answer, and a failed fetch
// still settles so nothing waits on it forever.
export const selectIsLoanBundleHistorySettled = createSelector(
  selectLoanBundleHistoryState,
  loanBundleHistory => !!loanBundleHistory?.isSettled,
);

// Disclosures are "sent" once at least one form-delivery (bundle) has actually
// been delivered — a delivered record carries a createdDate (scheduled-only
// records carry just scheduledDeliveryDate).
export const selectIsDisclosureSent = createSelector(selectLoanBundleHistory, loanBundleHistory =>
  (loanBundleHistory ?? []).some(({ createdDate }) => !!createdDate),
);

export const selectLoanBundleHistorySortedByDate = createSelector(
  [selectLoanBundleHistory, selectFormBundles],
  (loanBundleHistory, formBundles) => {
    // `Array.prototype.sort` mutates in place and returns the SAME reference — calling it
    // directly on `loanBundleHistory` mutates the Redux state array held by
    // `loanBundleHistoryReducer` out from under it. Because `formBundles` is a NEW object
    // reference on every fetch (`formBundlesReducer`'s GET_BUNDLES case replaces the whole
    // state), this selector recomputes — and re-mutates the same shared array in place — on
    // every poll cycle (DisclosureDelivery polls every 15s) even though the history itself
    // hasn't changed, corrupting whatever else holds that reference. Copy before sorting.
    return [...(loanBundleHistory ?? [])]
      .sort(
        (a, b) =>
          new Date(a.createdDate || a.scheduledDeliveryDate) -
          new Date(b.createdDate || b.scheduledDeliveryDate),
      )
      .map(history => ({
        ...history,
        formBundleName: formBundles[history.formBundleId]?.name,
      }));
  },
);
